import { isNil } from 'lodash/fp';
import { Suspense, useMemo } from 'react';
import { useSelector } from 'app/lib/hooks';
import { selectError } from 'app/store/error';
import { selectIsInMaintenance, selectIsSuspended } from 'app/store/features/serverStatus';
import { selectStartupQueriesDone } from 'app/store/startup';
import { selectIsUserFullyAuthenticated, selectUserHasAcceptedTerms } from 'app/store/users';
import { HTTP_FORBIDDEN } from 'app/middleware/constants';
import { Spinner } from 'app/components/core/Spinner';
import { ConnectionLostOverlayContainer } from 'app/components/features/AppOverlays';
import { StartUpContainer } from 'app/components/features/StartUp';
import { lazyWithRetry } from 'app/lib/utils/lazyWithRetry';

const LazyServerCondition = lazyWithRetry(() => import('app/components/composite/ServerCondition'));
const LazyAccessDeniedDialog = lazyWithRetry(
    () => import('app/components/features/AccessDeniedDialog')
);
const LazyApp = lazyWithRetry(() => import('app/components/features/App'));
const LazyAcceptEulaContainer = lazyWithRetry(
    () => import('app/components/features/AcceptEulaDialog')
);

export function Auth() {
    const userHasAcceptedTerms = useSelector(selectUserHasAcceptedTerms);
    const isInMaintenance = useSelector(selectIsInMaintenance);
    const isSuspended = useSelector(selectIsSuspended);
    const isUserFullyAuthenticated = useSelector(selectIsUserFullyAuthenticated);
    const isStartupDone = useSelector(selectStartupQueriesDone);
    const error = useSelector(selectError);
    const cachedApp = useMemo(() => {
        // We need to wait for the actual status from either the heartbeat
        // on first connect or the healthcheck before we can continue
        // `null` implicates we are not connected yet
        if (isNil(isSuspended) || isNil(isInMaintenance)) {
            return null;
        }

        if (isSuspended || isInMaintenance) {
            return (
                <LazyServerCondition isSuspended={isSuspended} isInMaintenance={isInMaintenance} />
            );
        }

        if (!isUserFullyAuthenticated || !isStartupDone) {
            return <StartUpContainer />;
        }

        if (!userHasAcceptedTerms) {
            return <LazyAcceptEulaContainer />;
        }

        if (error?.code === HTTP_FORBIDDEN) {
            return <LazyAccessDeniedDialog />;
        }

        return <LazyApp />;
    }, [
        isSuspended,
        isInMaintenance,
        isUserFullyAuthenticated,
        isStartupDone,
        userHasAcceptedTerms,
        error?.code
    ]);

    return (
        <Suspense fallback={<Spinner />}>
            {cachedApp}
            <ConnectionLostOverlayContainer />
        </Suspense>
    );
}
